/**
 * DowngradeBanner
 * ---------------------------------------------------------------
 * Persistent yellow banner shown to owners whose paid subscription was
 * recently cancelled (owner-initiated, admin-initiated, or Stripe dunning
 * exhaustion). Displays how many employees were auto-archived and gives
 * a one-click "Reactivate" that scrolls to the Plans section on Settings.
 *
 * Backend contract: `/api/subscription/status` returns
 * `downgrade_state: { plan_ended_at, auto_archived_count, ... }` when
 * relevant, or `null` otherwise. We render nothing when null.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";

const DISMISS_KEY = "downgrade_banner_dismissed_at";

export const DowngradeBanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Owners are the only role that can act on this. Managers /
    // employees would just see noise, so we skip the fetch entirely.
    if (!user || user.role !== "owner") return;
    apiClient.get("/subscription/status").then((res) => {
      setState(res.data?.downgrade_state || null);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && state?.plan_ended_at) {
      // Auto-un-dismiss when a NEW downgrade event happens.
      if (new Date(dismissedAt) < new Date(state.plan_ended_at)) {
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    }
  }, [state]);

  if (!state || dismissed) return null;

  const archived = state.auto_archived_count || 0;
  const endedDate = state.plan_ended_at
    ? new Date(state.plan_ended_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setDismissed(true);
  };

  return (
    <div
      data-testid="downgrade-banner"
      className="border border-amber-300 bg-amber-50 rounded-md px-4 py-3 mb-4 flex items-start gap-3"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm text-amber-900">
        <p className="font-medium">
          Your subscription ended on {endedDate}. Your data is safe.
        </p>
        <p className="text-xs mt-1 leading-relaxed">
          {archived > 0 ? (
            <>
              <span className="font-semibold">{archived}</span> employee{archived === 1 ? " was" : "s were"} auto-archived to fit the Free plan cap (3 active). Reactivate any paid plan to restore them automatically.
            </>
          ) : (
            <>You&apos;re back on the Free plan (3 employees max). Reactivate to unlock Time Clock kiosk, more employees, and other Pro features.</>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={() => navigate("/settings?tab=plan")}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="downgrade-banner-reactivate-btn"
        >
          Reactivate <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <button
          onClick={handleDismiss}
          className="text-amber-600 hover:text-amber-800 p-1"
          data-testid="downgrade-banner-dismiss-btn"
          aria-label="Dismiss banner"
          title="Hide until next update"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default DowngradeBanner;
